import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Upload, Sparkles } from "lucide-react";
import { InlineSkeleton } from "@/components/LoadingSkeleton";

const DEPTS = [
  { key: "content", label: "Content" },
  { key: "seo", label: "SEO" },
  { key: "marketing", label: "Marketing" },
  { key: "hr", label: "HR" },
];

/**
 * DayCardsManager — admin dialog for uploading and pruning per-department
 * Day Cards. Opened from the ⚙ button on the Dashboard's Day Card header
 * (visible only to Super Admin / Admin / Department Admin).
 *
 * The dialog supports:
 *   1. **Add single** — quick inline entry ("Add card")
 *   2. **Bulk upload**  — paste one message per line into a textarea, batch
 *      inserted via POST /day-cards/bulk (dedupes + strips blanks server-side)
 *   3. **Delete**       — trash icon on each row (per-card DELETE)
 *
 * Non-admins can't open this dialog — Dashboard hides the gear.
 */
export default function DayCardsManager({ open, onClose, currentUser, initialDepartment }) {
  const isAdmin = ["super_admin", "admin"].includes(currentUser?.role);
  const isDeptAdmin = currentUser?.role === "department_admin";
  const availableDepts = useMemo(() => {
    if (isAdmin) return DEPTS;
    if (isDeptAdmin) {
      const my = currentUser?.departments || (currentUser?.department ? [currentUser.department] : []);
      return DEPTS.filter((d) => my.includes(d.key));
    }
    return [];
  }, [isAdmin, isDeptAdmin, currentUser]);

  const [dept, setDept] = useState(initialDepartment || availableDepts[0]?.key || "content");
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newMsg, setNewMsg] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  // Iter 36.9.7 — bulk-select + delete. `selected` is a Set of card ids.
  // Reset whenever the department switches (department scoping).
  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => { setSelected(new Set()); }, [dept]);
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = cards.length > 0 && selected.size === cards.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(cards.map((c) => c.id)));
  };

  useEffect(() => {
    if (initialDepartment) setDept(initialDepartment);
  }, [initialDepartment]);

  const load = React.useCallback(async () => {
    if (!open || !dept) return;
    setLoading(true);
    try {
      const { data } = await api.get("/day-cards", { params: { department: dept } });
      setCards(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [open, dept]);

  useEffect(() => { load(); }, [load]);

  const addOne = async () => {
    const m = newMsg.trim();
    if (!m) return;
    try {
      await api.post("/day-cards", { message: m, department: dept });
      toast.success("Day card added");
      setNewMsg("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkUpload = async () => {
    const messages = bulkText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (!messages.length) { toast.error("Paste at least one message"); return; }
    try {
      const { data } = await api.post("/day-cards/bulk", { department: dept, messages });
      toast.success(`${data.inserted} card${data.inserted === 1 ? "" : "s"} added`);
      setBulkText("");
      setBulkOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/day-cards/${id}`);
      setCards((prev) => prev.filter((c) => c.id !== id));
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} card${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    try {
      const { data } = await api.post("/day-cards/bulk-delete", { ids });
      toast.success(`${data.deleted} card${data.deleted === 1 ? "" : "s"} deleted`);
      setCards((prev) => prev.filter((c) => !selected.has(c.id)));
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="day-cards-manager">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> Manage Day Cards</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-500">Department</label>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="h-9 w-52" data-testid="day-cards-dept"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableDepts.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400 ml-auto">{cards.length} card{cards.length === 1 ? "" : "s"}</span>
          </div>

          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-2">
            <Input
              value={newMsg}
              onChange={(e) => setNewMsg(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOne(); } }}
              placeholder="Write a new day-card message and press Enter…"
              className="border-slate-200 flex-1"
              maxLength={280}
              data-testid="day-cards-new-msg"
            />
            <Button size="sm" onClick={addOne} disabled={!newMsg.trim()} className="bg-purple-600 hover:bg-purple-700" data-testid="day-cards-add-btn">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkOpen((b) => !b)} data-testid="day-cards-bulk-toggle">
              <Upload className="h-4 w-4 mr-1" /> Bulk upload
            </Button>
          </div>

          {bulkOpen && (
            <div className="border border-slate-200 rounded-md p-3 bg-white">
              <div className="text-xs text-slate-500 mb-2">Paste one message per line — blanks and duplicates auto-skipped.</div>
              <Textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={8}
                placeholder={"Make it happen.\nStrong minds discuss ideas.\nProgress > perfection.\n…"}
                className="font-mono text-xs"
                data-testid="day-cards-bulk-text"
              />
              <div className="flex justify-end mt-2 gap-2">
                <Button size="sm" variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={bulkUpload} className="bg-purple-600 hover:bg-purple-700" data-testid="day-cards-bulk-submit">
                  Upload {bulkText.split(/\r?\n/).filter((s) => s.trim()).length} card{bulkText.split(/\r?\n/).filter((s) => s.trim()).length === 1 ? "" : "s"}
                </Button>
              </div>
            </div>
          )}

          <div className="border border-slate-200 rounded-md bg-white max-h-[45vh] overflow-y-auto">
            {loading && (
              <div className="p-3 space-y-2" data-testid="day-cards-skeleton">
                <InlineSkeleton width="w-3/4" />
                <InlineSkeleton width="w-1/2" />
                <InlineSkeleton width="w-2/3" />
              </div>
            )}
            {!loading && cards.length === 0 && (
              <div className="text-sm text-slate-400 py-8 text-center">No day cards yet — add your first above.</div>
            )}
            {/* Iter 36.9.7 — Bulk-select header row. Sticky so it stays
                visible while the caller scrolls a long list. Delete button
                only renders when at least one card is selected. */}
            {!loading && cards.length > 0 && (
              <div className="sticky top-0 z-10 flex items-center gap-3 px-3 py-2 border-b border-slate-200 bg-slate-50/95 backdrop-blur">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  aria-label="Select all"
                  data-testid="day-cards-select-all"
                />
                <span className="text-xs text-slate-500">
                  {selected.size > 0 ? `${selected.size} selected` : "Select all"}
                </span>
                {selected.size > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={bulkDelete}
                    className="ml-auto h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                    data-testid="day-cards-bulk-delete"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete {selected.size}
                  </Button>
                )}
              </div>
            )}
            {!loading && cards.map((c) => (
              <div key={c.id} className="flex items-start gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50" data-testid={`day-card-row-${c.id}`}>
                <Checkbox
                  checked={selected.has(c.id)}
                  onCheckedChange={() => toggleOne(c.id)}
                  aria-label={`Select card ${c.id}`}
                  data-testid={`day-card-select-${c.id}`}
                  className="mt-0.5"
                />
                <div className="flex-1 text-sm text-slate-800">{c.message}</div>
                <button onClick={() => remove(c.id)} className="text-slate-400 hover:text-rose-500" data-testid={`day-card-delete-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="day-cards-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
